import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateProfile,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signInWithCustomToken,
} from 'firebase/auth';
import { auth } from './firebase';
import { api } from './api';

// Auth state observer
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Get current user
export const getCurrentUser = () => {
  return auth.currentUser;
};

// Sign in with email
export const signIn = async (email, password) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
};

// Sign up with email — simple account creation without role
export const signUp = async (email, password, displayName) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  await updateProfile(user, { displayName });

  const result = await api.createUser({ displayName, email });
  if (result.error) {
    console.error('Error creating user:', result.error);
  }

  return user;
};

// --- Phone auth (Firebase SMS OTP) ---

export const PHONE_DIALS = [
  { code: 'NG', dial: '+234', label: 'Nigeria (+234)' },
  { code: 'GH', dial: '+233', label: 'Ghana (+233)' },
  { code: 'KE', dial: '+254', label: 'Kenya (+254)' },
  { code: 'US', dial: '+1', label: 'US / Intl (+1)' },
];

/** Build E.164 from dial code + local national number. */
export const toE164 = (dialCode, nationalNumber) => {
  const dial = String(dialCode || '').replace(/\s/g, '');
  let national = String(nationalNumber || '').replace(/[^\d]/g, '');
  // Drop a leading 0 common in NG/GH/KE national formats
  if (national.startsWith('0')) national = national.slice(1);
  if (!dial.startsWith('+') || !national) return null;
  return `${dial}${national}`;
};

export const isValidE164 = (phone) => /^\+[1-9]\d{7,14}$/.test(phone || '');

export const isPhoneAuthTestMode = () =>
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_PHONE_AUTH_TEST_MODE === 'true';

const RECAPTCHA_CONTAINER_ID = 'spirara-phone-recaptcha';

let phoneRecaptchaVerifier = null;

const isRecaptchaRenderError = (err) =>
  String(err?.message || '').includes('already been rendered') ||
  err?.code === 'auth/captcha-check-failed';

/** Persistent off-DOM-tree mount — avoids React remount / StrictMode double-render issues. */
const getPhoneRecaptchaContainer = () => {
  if (typeof document === 'undefined') {
    throw new Error('reCAPTCHA is only available in the browser');
  }
  let el = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = RECAPTCHA_CONTAINER_ID;
    el.style.cssText =
      'position:fixed;left:0;top:0;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none';
    document.body.appendChild(el);
  }
  return el;
};

const clearPhoneRecaptcha = async () => {
  if (phoneRecaptchaVerifier) {
    try {
      await phoneRecaptchaVerifier.clear();
    } catch {
      // Widget may already be cleared
    }
  }
  phoneRecaptchaVerifier = null;
  const el = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (el) {
    el.innerHTML = '';
  }
};

const createPhoneRecaptcha = (container) =>
  new RecaptchaVerifier(auth, container, {
    size: 'invisible',
    callback: () => {},
    'expired-callback': () => {},
  });

/** Reset reCAPTCHA state (e.g. resend or change number). */
export const resetPhoneRecaptcha = clearPhoneRecaptcha;

/**
 * Send SMS OTP. Uses a single persistent invisible reCAPTCHA container.
 * Creates a fresh verifier on every call to avoid "already been rendered" errors.
 */
export const requestPhoneOtp = async (phoneE164) => {
  if (!isValidE164(phoneE164)) {
    const error = new Error('Enter a valid phone number with country code.');
    error.code = 'auth/invalid-phone-number';
    throw error;
  }

  const send = async () => {
    await clearPhoneRecaptcha();
    const container = getPhoneRecaptchaContainer();
    phoneRecaptchaVerifier = createPhoneRecaptcha(container);
    return signInWithPhoneNumber(auth, phoneE164, phoneRecaptchaVerifier);
  };

  try {
    return await send();
  } catch (err) {
    await clearPhoneRecaptcha();
    if (isRecaptchaRenderError(err)) {
      // One retry with a brand-new container element
      const stale = document.getElementById(RECAPTCHA_CONTAINER_ID);
      stale?.remove();
      return send();
    }
    throw err;
  }
};

/** @deprecated Use requestPhoneOtp — kept for compatibility */
export const createInvisibleRecaptcha = createPhoneRecaptcha;

/** @deprecated Use resetPhoneRecaptcha */
export const clearRecaptcha = clearPhoneRecaptcha;

/** @deprecated Use requestPhoneOtp */
export const sendPhoneVerification = async (phoneE164, recaptchaVerifier) => {
  if (!isValidE164(phoneE164)) {
    const error = new Error('Enter a valid phone number with country code.');
    error.code = 'auth/invalid-phone-number';
    throw error;
  }
  return signInWithPhoneNumber(auth, phoneE164, recaptchaVerifier);
};

/**
 * Confirm SMS code and ensure a backend user document exists.
 * Firebase creates the Auth user on first successful phone confirm.
 */
export const confirmPhoneVerification = async (
  confirmationResult,
  code,
  { displayName } = {}
) => {
  if (!confirmationResult) {
    const error = new Error('Request a verification code first.');
    error.code = 'auth/missing-confirmation';
    throw error;
  }

  const trimmed = String(code || '').trim();
  if (!/^\d{6}$/.test(trimmed)) {
    const error = new Error('Enter the 6-digit code from your SMS.');
    error.code = 'auth/invalid-verification-code';
    throw error;
  }

  const userCredential = await confirmationResult.confirm(trimmed);
  const user = userCredential.user;
  const isNewUser = Boolean(userCredential.additionalUserInfo?.isNewUser);

  if (displayName?.trim() && !user.displayName) {
    try {
      await updateProfile(user, { displayName: displayName.trim() });
    } catch (err) {
      console.warn('Could not set display name:', err);
    }
  }

  await ensureUserDocument({
    displayName: displayName?.trim() || user.displayName || '',
    email: user.email || '',
    phone: user.phoneNumber || '',
  });

  return { user, isNewUser };
};

export const ensureUserDocument = async ({ displayName, email, phone } = {}) => {
  const result = await api.createUser({
    displayName: displayName || '',
    email: email || '',
    phone: phone || '',
  });
  if (result.error) {
    console.error('Error ensuring user document:', result.error);
  }
  return result.data || null;
};

/** True when phone signup failed for reasons the user cannot fix on the phone form. */
export const shouldOfferEmailSignup = (err) => {
  const code = err?.code || '';
  if (code === 'auth/missing-display-name' || code === 'auth/invalid-phone-number') {
    return false;
  }
  return true;
};

const maskPhoneForLog = (phone) => {
  const value = String(phone || '').trim();
  if (!value) return '(none)';
  if (value.length <= 6) return '***';
  return `${value.slice(0, 4)}***${value.slice(-2)}`;
};

/** Log phone signup failure to console + backend (fire-and-forget). */
export const logPhoneSignupFailure = (err, { step, phone, locale } = {}) => {
  const payload = {
    code: err?.code || 'unknown',
    message: err?.message || String(err),
    step: step || 'unknown',
    phone: maskPhoneForLog(phone),
    locale: locale || '',
    offeredEmail: shouldOfferEmailSignup(err),
  };
  console.warn('[auth] phone signup failed', payload);
  api.logPhoneSignupFailure(payload).catch(() => {});
};

export const mapAuthError = (err) => {
  const code = err?.code || '';
  switch (code) {
    case 'auth/email-already-in-use':
      return 'This email is already registered. Please sign in instead.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/weak-password':
      return 'Password is too weak. Please use at least 6 characters.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password.';
    case 'auth/invalid-phone-number':
      return 'That phone number looks invalid. Check the country code and try again.';
    case 'auth/missing-phone-number':
      return 'Enter your phone number to continue.';
    case 'auth/quota-exceeded':
      return 'SMS quota exceeded. Please try again later or use email.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'auth/captcha-check-failed':
      return 'Security check failed. Refresh the page and try again.';
    case 'auth/invalid-verification-code':
      return 'That code is incorrect. Check the SMS and try again.';
    case 'auth/code-expired':
      return 'That code has expired. Request a new one.';
    case 'auth/missing-confirmation':
      return 'Request a verification code first.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    case 'auth/operation-not-allowed':
      return 'Phone sign-in is unavailable right now. Please sign up with email instead.';
    default:
      if (String(err?.message || '').includes('already been rendered')) {
        return 'Security check failed. Refresh the page and try again.';
      }
      return err?.message || 'Something went wrong. Please try again.';
  }
};

// Sign out
export const logOut = async () => {
  await signOut(auth);
};

// Reset password
export const resetPassword = async (email) => {
  await sendPasswordResetEmail(auth, email);
};

// Get user data from backend API
export const getUserData = async (uid) => {
  const result = await api.getUserData(uid);
  if (result.error) {
    console.error('Error getting user data:', result.error);
    return null;
  }
  return result.data;
};

// Sign in with a Firebase custom token (admin impersonation)
export const signInWithToken = async (customToken) => {
  const userCredential = await signInWithCustomToken(auth, customToken);
  return userCredential.user;
};

// Check if user has role
export const hasRole = (user, role) => {
  if (!user) return false;
  if (Array.isArray(user.role)) {
    return user.role.includes(role);
  }
  return user.role === role;
};

// Check if user has any of the roles
export const hasAnyRole = (user, roles) => {
  if (!user) return false;
  if (Array.isArray(user.role)) {
    return roles.some((role) => user.role.includes(role));
  }
  return roles.includes(user.role);
};
